const HERO_FIXED_PANEL_CLASS = "marketplace-facet-dropdown__panel--hero-fixed";

interface FacetDropdownEntry {
    dropdown: HTMLElement;
    trigger: HTMLButtonElement;
    panel: HTMLElement;
}

/** Panels clipped by overflow (hero bar or action-bar horizontal scroll). */
function usesFixedPanels(dropdown: HTMLElement): boolean {
    return (
        dropdown.closest("#hero-filter-bar") !== null ||
        dropdown.closest(".marketplace-action-bar__chips") !== null
    );
}

function positionFixedPanel(
    trigger: HTMLButtonElement,
    panel: HTMLElement,
): void {
    const rect = trigger.getBoundingClientRect();
    const panelWidth = Math.min(352, window.innerWidth - 24);
    const top = rect.bottom + 8;
    const maxHeight = Math.min(352, window.innerHeight - top - 16);

    panel.classList.add(HERO_FIXED_PANEL_CLASS);
    panel.style.width = `${panelWidth}px`;
    panel.style.maxHeight = `${maxHeight}px`;
    panel.style.left = `${Math.max(
        12,
        Math.min(rect.left, window.innerWidth - panelWidth - 12),
    )}px`;
    panel.style.top = `${top}px`;
}

function resetHeroFixedPanel(panel: HTMLElement): void {
    panel.classList.remove(HERO_FIXED_PANEL_CLASS);
    panel.style.width = "";
    panel.style.maxHeight = "";
    panel.style.left = "";
    panel.style.top = "";
}

/**
 * Bouton encore à l'écran, sous l'en-tête collé : sinon son panneau flotterait sans lui (sur les
 * landings, la barre n'est collée que dans le bloc annonces).
 */
export function isTriggerOnScreen(trigger: HTMLElement): boolean {
    const rect = trigger.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;
    const headerBottom = Math.max(
        0,
        document.getElementById("app-header")?.getBoundingClientRect().bottom ?? 0,
    );

    return rect.bottom > headerBottom && rect.top < window.innerHeight;
}

/** Une zone du panneau, sous `target`, peut encore défiler dans ce sens. */
export function canScrollInsidePanel(
    target: EventTarget | null,
    panel: HTMLElement,
    deltaY: number,
): boolean {
    if (deltaY === 0) return true;
    for (
        let el = target instanceof Element ? target : null;
        el && panel.contains(el);
        el = el.parentElement
    ) {
        if (!/(auto|scroll)/.test(getComputedStyle(el).overflowY)) continue;
        const room =
            deltaY < 0
                ? el.scrollTop
                : el.scrollHeight - el.clientHeight - el.scrollTop;
        if (room > 1) return true;
    }

    return false;
}

/** Molette à retenir sur le panneau : jamais un zoom (Ctrl/Cmd + molette, pincement du pavé tactile). */
export function shouldBlockPanelWheel(
    event: Pick<WheelEvent, "ctrlKey" | "metaKey" | "deltaX" | "deltaY" | "target">,
    panel: HTMLElement,
): boolean {
    if (event.ctrlKey || event.metaKey) return false;
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return false;

    return !canScrollInsidePanel(event.target, panel, event.deltaY);
}

/**
 * Molette ou doigt sur le panneau ouvert : seule sa liste défile, jamais la page (sur le champ de
 * recherche ou en butée de liste, le navigateur passerait sinon le mouvement à la page).
 */
function containPanelScroll(panel: HTMLElement): void {
    panel.addEventListener(
        "wheel",
        (event) => {
            if (shouldBlockPanelWheel(event, panel)) event.preventDefault();
        },
        { passive: false },
    );

    let lastTouchY: number | null = null;
    panel.addEventListener(
        "touchstart",
        (event) => {
            lastTouchY = event.touches.length === 1 ? event.touches[0].clientY : null;
        },
        { passive: true },
    );
    panel.addEventListener(
        "touchmove",
        (event) => {
            if (lastTouchY === null || event.touches.length !== 1) return;
            const y = event.touches[0].clientY;
            const deltaY = lastTouchY - y;
            lastTouchY = y;
            if (event.cancelable && !canScrollInsidePanel(event.target, panel, deltaY)) {
                event.preventDefault();
            }
        },
        { passive: false },
    );
}

/**
 * Marge de fin des pastilles (padding-right de 20 px) + arrondis : l'aimantation peut s'arrêter sur le
 * début de la dernière pastille, quelques pixels avant le vrai bout, alors qu'elle est déjà entière.
 */
const CHIPS_END_SLACK_PX = 24;

/** Pastilles défilées jusqu'au bout : le fondu de droite n'a plus rien à signaler. */
export function isChipsScrollEnd(
    chips: Pick<HTMLElement, "scrollLeft" | "clientWidth" | "scrollWidth">,
): boolean {
    return chips.scrollLeft + chips.clientWidth >= chips.scrollWidth - CHIPS_END_SLACK_PX;
}

/**
 * Fondus des pastilles (masque CSS) : à droite au début (`is-scroll-start`), des deux côtés au milieu,
 * à gauche à la fin (`is-scroll-end`), aucun s'il n'y a rien à faire défiler. Mis à jour :
 * au défilement, au redimensionnement et quand une pastille change de largeur (valeur affichée,
 * « Prix en baisse » monté après coup).
 */
function initChipsScrollEnd(): void {
    document
        .querySelectorAll<HTMLElement>(".marketplace-action-bar__chips")
        .forEach((chips) => {
            const update = (): void => {
                chips.classList.toggle("is-scroll-end", isChipsScrollEnd(chips));
                chips.classList.toggle("is-scroll-start", chips.scrollLeft <= 1);
            };
            chips.addEventListener("scroll", update, { passive: true });
            window.addEventListener("resize", update, { passive: true });
            if (typeof ResizeObserver !== "undefined") {
                const observer = new ResizeObserver(update);
                observer.observe(chips);
                Array.from(chips.children).forEach((child) => observer.observe(child));
            }
            update();
        });
}

let sortFocusRingBound = false;

/**
 * Contour de focus des « Trier » au <select> transparent (barre, titre, panneau) : seulement si le focus
 * vient du clavier (Tab), jamais d'un toucher ou d'un clic, où `:focus-visible` s'allume aussi sur un <select>.
 */
export function initSortFocusRing(): void {
    if (sortFocusRingBound || typeof document === "undefined") return;
    sortFocusRingBound = true;

    let keyboard = false;
    document.addEventListener(
        "keydown",
        () => {
            keyboard = true;
        },
        true,
    );
    document.addEventListener(
        "pointerdown",
        () => {
            keyboard = false;
        },
        true,
    );

    const isSortSelect = (target: EventTarget | null): target is HTMLSelectElement =>
        target instanceof HTMLSelectElement &&
        target.classList.contains("hits-sort-by__select-native");

    document.addEventListener("focusin", (event) => {
        if (!isSortSelect(event.target)) return;
        event.target.closest(".hits-sort-by__native-wrap")?.classList.toggle("is-focus-visible", keyboard);
    });
    document.addEventListener("focusout", (event) => {
        if (!isSortSelect(event.target)) return;
        event.target.closest(".hits-sort-by__native-wrap")?.classList.remove("is-focus-visible");
    });
}

/**
 * Pastille ou « Filtrer » touché avant le chargement du JS (script critique de `action-bar.blade.php`) :
 * à appeler une fois les boutons branchés, le dernier appui est rejoué (le panneau s'ouvre).
 */
/**
 * Barre branchée : arrête de retenir les appuis (le suivant va directement aux écouteurs) et rend
 * l'appui retenu, à rejouer avec `replayActionBarTap()`.
 */
export function takePendingActionBarTap(): HTMLElement | null {
    const pending = typeof window !== "undefined" ? window.__mktBarTap : undefined;
    if (!pending || pending.ready) return null;
    pending.ready = true;
    const target = pending.el;
    pending.el = null;
    target?.classList.remove("is-pending-tap");

    return target;
}

/** Rejoue un appui retenu ; une pastille redessinée entre-temps (barre e3) est retrouvée par sa valeur. */
export function replayActionBarTap(target: HTMLElement | null): void {
    if (!target) return;
    if (target.isConnected) {
        target.click();
        return;
    }
    const { chipKind, chipValue } = target.dataset;
    if (chipKind && chipValue !== undefined) {
        Array.from(document.querySelectorAll<HTMLElement>("[data-mkt-e3-chips] [data-chip-kind]"))
            .find((chip) => chip.dataset.chipKind === chipKind && chip.dataset.chipValue === chipValue)
            ?.click();
    }
}

export function replayPendingActionBarTap(): void {
    replayActionBarTap(takePendingActionBarTap());
}

/**
 * Tablet+ facet dropdowns in the marketplace action bar (e.g. Marque on /marketplace2).
 */
export function initActionBarFacetDropdowns(): void {
    initChipsScrollEnd();
    initSortFocusRing();

    const entries: FacetDropdownEntry[] = [];

    document.querySelectorAll<HTMLElement>("[data-facet-dropdown]").forEach(
        (dropdown) => {
            const trigger = dropdown.querySelector<HTMLButtonElement>(
                "[data-facet-dropdown-trigger]",
            );
            const panel = dropdown.querySelector<HTMLElement>(
                "[data-facet-dropdown-panel]",
            );
            if (!trigger || !panel) return;
            entries.push({ dropdown, trigger, panel });
        },
    );

    if (!entries.length) return;

    const heroFixedPanels = new Map<HTMLElement, HTMLButtonElement>();
    const heroPanelAnchors = new Map<HTMLElement, Comment>();

    const portalHeroPanel = (panel: HTMLElement): void => {
        if (heroPanelAnchors.has(panel)) {
            return;
        }

        const anchor = document.createComment("hero-facet-panel");
        panel.parentNode?.insertBefore(anchor, panel);
        heroPanelAnchors.set(panel, anchor);
        document.body.appendChild(panel);
    };

    const restoreHeroPanel = (panel: HTMLElement): void => {
        const anchor = heroPanelAnchors.get(panel);
        if (!anchor?.parentNode) {
            return;
        }

        anchor.parentNode.insertBefore(panel, anchor);
        anchor.remove();
        heroPanelAnchors.delete(panel);
    };

    const syncHeroFixedPanels = (): void => {
        heroFixedPanels.forEach((trigger, panel) => {
            if (!panel.hidden) {
                positionFixedPanel(trigger, panel);
            }
        });
    };

    // Page qui défile : un panneau dont le bouton est sorti de l'écran se ferme.
    const closePanelsWithTriggerOffScreen = (): void => {
        entries.forEach(({ trigger, panel }) => {
            if (!panel.hidden && !isTriggerOnScreen(trigger)) {
                panel.hidden = true;
                trigger.setAttribute("aria-expanded", "false");
                releaseHeroPanel(panel);
            }
        });
    };

    const releaseHeroPanel = (panel: HTMLElement): void => {
        resetHeroFixedPanel(panel);
        restoreHeroPanel(panel);
        heroFixedPanels.delete(panel);
    };

    const closeAll = (except?: HTMLElement) => {
        entries.forEach(({ dropdown, trigger, panel }) => {
            if (dropdown === except) return;
            panel.hidden = true;
            trigger.setAttribute("aria-expanded", "false");
            releaseHeroPanel(panel);
        });
    };

    const isClickInsideDropdown = (
        { dropdown, panel }: FacetDropdownEntry,
        target: Node,
    ): boolean => {
        if (dropdown.contains(target)) {
            return true;
        }

        return !panel.hidden && panel.contains(target);
    };

    entries.forEach(({ dropdown, trigger, panel }) => {
        trigger.addEventListener("click", (event) => {
            event.stopPropagation();
            const willOpen = panel.hidden;
            closeAll(willOpen ? dropdown : undefined);
            panel.hidden = !willOpen;
            trigger.setAttribute("aria-expanded", String(willOpen));

            if (willOpen && usesFixedPanels(dropdown)) {
                portalHeroPanel(panel);
                heroFixedPanels.set(panel, trigger);
                positionFixedPanel(trigger, panel);
            } else {
                releaseHeroPanel(panel);
            }
        });

        panel.addEventListener("click", (event) => {
            event.stopPropagation();
        });

        containPanelScroll(panel);
    });

    window.addEventListener("resize", syncHeroFixedPanels);
    window.addEventListener(
        "scroll",
        () => {
            closePanelsWithTriggerOffScreen();
            syncHeroFixedPanels();
        },
        true,
    );

    // Horizontal slide of the action-bar chips must dismiss open facet panels
    // (otherwise they stay anchored while their trigger scrolls away).
    document
        .querySelectorAll<HTMLElement>(".marketplace-action-bar__chips")
        .forEach((chipsEl) => {
            chipsEl.addEventListener(
                "scroll",
                () => {
                    closeAll();
                },
                { passive: true },
            );
        });

    document.addEventListener("click", (event) => {
        const target = event.target;
        if (!(target instanceof Node)) return;
        const clickedInside = entries.some((entry) =>
            isClickInsideDropdown(entry, target),
        );
        if (!clickedInside) closeAll();
    });
    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") closeAll();
    });
}
